'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Plus, Search, X, Edit2, Trash2, Clock, Loader2, Phone, CreditCard, User,  } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import {
  securityStaffService, securityShiftsService,
  type SecurityStaff, type SecurityShift, type ShiftType,
} from '@/lib/supabase/services';
import ImageUpload from '@/components/ui/ImageUpload';
import AppImage from '@/components/ui/AppImage';

const SHIFT_COLORS: Record<ShiftType, string> = {
  'صباحية': 'bg-warning/15 text-warning border-warning/30',
  'مسائية': 'bg-info/15 text-info border-info/30',
  'ليلية': 'bg-muted text-muted-foreground border-border',
};

const GATES = ['البوابة الرئيسية', 'بوابة الشاحنات', 'البوابة الخلفية'];
const SHIFTS: ShiftType[] = ['صباحية', 'مسائية', 'ليلية'];
const SHIFT_TIMES: Record<ShiftType, { start: string; end: string }> = {
  'صباحية': { start: '06:00', end: '14:00' },
  'مسائية': { start: '14:00', end: '22:00' },
  'ليلية': { start: '22:00', end: '06:00' },
};

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

type ActiveTab = 'staff' | 'shifts';

export default function SecurityManagementContent() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('staff');
  const [staffList, setStaffList] = useState<SecurityStaff[]>([]);
  const [shifts, setShifts] = useState<SecurityShift[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Staff form
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState<SecurityStaff | null>(null);
  const [staffForm, setStaffForm] = useState({ name: '', nationalId: '', mobile: '', idCardImageUrl: '', isActive: true });
  const [staffSubmitting, setStaffSubmitting] = useState(false);

  // Shift form
  const [showShiftForm, setShowShiftForm] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    staffId: '', shiftType: 'صباحية' as ShiftType,
    shiftDate: new Date().toISOString().split('T')[0],
    startTime: '06:00', endTime: '14:00', gate: 'البوابة الرئيسية', notes: '',
  });
  const [shiftSubmitting, setShiftSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [staffData, shiftsData] = await Promise.all([
        securityStaffService.getAll(),
        securityShiftsService.getAll(),
      ]);
      setStaffList(staffData);
      setShifts(shiftsData);
    } catch (err: any) {
      toast.error('فشل تحميل البيانات: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredStaff = staffList.filter((s) =>
    !searchQuery || s.name.includes(searchQuery) || s.nationalId.includes(searchQuery) || s.mobile.includes(searchQuery)
  );

  const filteredShifts = shifts.filter((s) =>
    !searchQuery || s.staffName?.includes(searchQuery) || s.shiftType.includes(searchQuery)
  );

  const openAddStaff = () => {
    setEditingStaff(null);
    setStaffForm({ name: '', nationalId: '', mobile: '', idCardImageUrl: '', isActive: true });
    setShowStaffForm(true);
  };

  const openEditStaff = (staff: SecurityStaff) => {
    setEditingStaff(staff);
    setStaffForm({ name: staff.name, nationalId: staff.nationalId, mobile: staff.mobile, idCardImageUrl: staff.idCardImageUrl, isActive: staff.isActive });
    setShowStaffForm(true);
  };

  const handleStaffSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!staffForm.name.trim() || !staffForm.nationalId.trim() || !staffForm.mobile.trim()) {
      toast.error('يرجى ملء جميع الحقول المطلوبة');
      return;
    }
    setStaffSubmitting(true);
    try {
      if (editingStaff) {
        await securityStaffService.update(editingStaff.id, staffForm);
        toast.success('تم تحديث بيانات فرد الأمن بنجاح');
      } else {
        await securityStaffService.create(staffForm);
        toast.success('تم إضافة فرد الأمن بنجاح');
      }
      setShowStaffForm(false);
      loadData();
    } catch (err: any) {
      toast.error('فشل الحفظ: ' + err.message);
    } finally {
      setStaffSubmitting(false);
    }
  };

  const handleDeleteStaff = async (id: string, name: string) => {
    if (!confirm(`هل أنت متأكد من حذف "${name}"؟`)) return;
    try {
      await securityStaffService.delete(id);
      toast.success('تم حذف فرد الأمن');
      loadData();
    } catch (err: any) {
      toast.error('فشل الحذف: ' + err.message);
    }
  };

  const handleShiftTypeChange = (type: ShiftType) => {
    setShiftForm((prev) => ({ ...prev, shiftType: type, startTime: SHIFT_TIMES[type].start, endTime: SHIFT_TIMES[type].end }));
  };

  const handleShiftSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shiftForm.staffId) { toast.error('يرجى اختيار فرد الأمن'); return; }
    setShiftSubmitting(true);
    try {
      await securityShiftsService.create(shiftForm);
      toast.success('تم إضافة الوردية بنجاح');
      setShowShiftForm(false);
      setShiftForm({ staffId: '', shiftType: 'صباحية', shiftDate: new Date().toISOString().split('T')[0], startTime: '06:00', endTime: '14:00', gate: 'البوابة الرئيسية', notes: '' });
      loadData();
    } catch (err: any) {
      toast.error('فشل الحفظ: ' + err.message);
    } finally {
      setShiftSubmitting(false);
    }
  };

  const handleDeleteShift = async (id: string) => {
    if (!confirm('هل أنت متأكد من حذف هذه الوردية؟')) return;
    try {
      await securityShiftsService.delete(id);
      toast.success('تم حذف الوردية');
      loadData();
    } catch (err: any) {
      toast.error('فشل الحذف: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 xl:p-6" dir="rtl">
      <Toaster position="bottom-right" richColors />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-accent/15 border border-accent/30 flex items-center justify-center">
            <Shield size={20} className="text-accent" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">إدارة أفراد الأمن والورديات</h1>
            <p className="text-sm text-muted-foreground">إدارة فريق الأمن وجداول الورديات</p>
          </div>
        </div>
        <button
          onClick={activeTab === 'staff' ? openAddStaff : () => setShowShiftForm(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-accent text-accent-foreground rounded-xl font-bold text-sm hover:opacity-90 transition-all active:scale-95"
        >
          <Plus size={16} />
          {activeTab === 'staff' ? 'إضافة فرد أمن' : 'إضافة وردية'}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">إجمالي الأفراد</p>
          <p className="text-3xl font-bold text-foreground tabular-nums">{staffList.length}</p>
        </div>
        <div className="bg-card border border-success/30 rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">نشطون</p>
          <p className="text-3xl font-bold text-success tabular-nums">{staffList.filter((s) => s.isActive).length}</p>
        </div>
        <div className="bg-card border border-warning/30 rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">ورديات اليوم</p>
          <p className="text-3xl font-bold text-warning tabular-nums">
            {shifts.filter((s) => s.shiftDate === new Date().toISOString().split('T')[0]).length}
          </p>
        </div>
        <div className="bg-card border border-info/30 rounded-xl p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">إجمالي الورديات</p>
          <p className="text-3xl font-bold text-info tabular-nums">{shifts.length}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 bg-card border border-border rounded-2xl p-1.5 w-fit">
        <button
          onClick={() => setActiveTab('staff')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ${activeTab === 'staff' ? 'bg-accent text-accent-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <User size={16} />
          أفراد الأمن
        </button>
        <button
          onClick={() => setActiveTab('shifts')}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ${activeTab === 'shifts' ? 'bg-accent text-accent-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <Clock size={16} />
          الورديات
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={activeTab === 'staff' ? 'بحث بالاسم أو الرقم القومي...' : 'بحث بالاسم أو نوع الوردية...'}
          className="w-full bg-card border border-border rounded-xl pr-9 pl-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="text-accent animate-spin" />
        </div>
      ) : activeTab === 'staff' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredStaff.length === 0 ? (
            <div className="col-span-full text-center py-16 text-muted-foreground">
              <Shield size={40} className="mx-auto mb-3 opacity-30" />
              <p>لا يوجد أفراد أمن مسجلون</p>
            </div>
          ) : filteredStaff.map((staff) => (
            <div key={staff.id} className="bg-card border border-border rounded-2xl p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-14 h-14 rounded-xl overflow-hidden border border-border shrink-0 bg-muted flex items-center justify-center">
                  {staff.idCardImageUrl ? (
                    <AppImage src={staff.idCardImageUrl} alt={`صورة بطاقة ${staff.name}`} className="w-full h-full object-cover" />
                  ) : (
                    <Shield size={20} className="text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-foreground text-sm truncate">{staff.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${staff.isActive ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'}`}>
                      {staff.isActive ? 'نشط' : 'غير نشط'}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <CreditCard size={11} />
                      {staff.nationalId}
                    </p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <Phone size={11} />
                      {staff.mobile}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-1 border-t border-border">
                <button
                  onClick={() => openEditStaff(staff)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-muted text-muted-foreground hover:text-foreground text-xs font-medium transition-all"
                >
                  <Edit2 size={12} />
                  تعديل
                </button>
                <button
                  onClick={() => handleDeleteStaff(staff.id, staff.name)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-danger/10 text-danger hover:bg-danger/20 text-xs font-medium transition-all"
                >
                  <Trash2 size={12} />
                  حذف
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">فرد الأمن</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">الوردية</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">التاريخ</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">الوقت</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">البوابة</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">إجراء</th>
                </tr>
              </thead>
              <tbody>
                {filteredShifts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-muted-foreground">
                      <Clock size={32} className="mx-auto mb-2 opacity-30" />
                      <p className="text-sm">لا توجد ورديات مسجلة</p>
                    </td>
                  </tr>
                ) : filteredShifts.map((shift) => (
                  <tr key={shift.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-foreground">{shift.staffName}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold border ${SHIFT_COLORS[shift.shiftType]}`}>
                        <Clock size={11} />
                        {shift.shiftType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground tabular-nums">{formatDate(shift.shiftDate)}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground tabular-nums">{shift.startTime} — {shift.endTime}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{shift.gate}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDeleteShift(shift.id)}
                        className="p-1.5 rounded-lg text-danger hover:bg-danger/10 transition-all"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Staff Form Modal */}
      {showStaffForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="font-bold text-foreground">{editingStaff ? 'تعديل فرد الأمن' : 'إضافة فرد أمن جديد'}</h2>
              <button onClick={() => setShowStaffForm(false)} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleStaffSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">الاسم الكامل *</label>
                <input
                  type="text"
                  value={staffForm.name}
                  onChange={(e) => setStaffForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="اسم فرد الأمن"
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">الرقم القومي *</label>
                <input
                  type="text"
                  value={staffForm.nationalId}
                  onChange={(e) => setStaffForm((p) => ({ ...p, nationalId: e.target.value }))}
                  placeholder="14 رقم"
                  maxLength={14}
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">رقم الموبايل *</label>
                <input
                  type="tel"
                  value={staffForm.mobile}
                  onChange={(e) => setStaffForm((p) => ({ ...p, mobile: e.target.value }))}
                  placeholder="01xxxxxxxxx"
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
              </div>
              <ImageUpload
                bucket="id-cards"
                currentUrl={staffForm.idCardImageUrl}
                onUpload={(url) => setStaffForm((p) => ({ ...p, idCardImageUrl: url }))}
                label="صورة البطاقة الشخصية"
                folder="security-staff"
              />
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={staffForm.isActive}
                  onChange={(e) => setStaffForm((p) => ({ ...p, isActive: e.target.checked }))}
                  className="w-4 h-4 accent-accent"
                />
                <label htmlFor="isActive" className="text-sm text-foreground">نشط</label>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={staffSubmitting}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-accent text-accent-foreground rounded-xl font-bold text-sm hover:opacity-90 transition-all disabled:opacity-50"
                >
                  {staffSubmitting && <Loader2 size={14} className="animate-spin" />}
                  {editingStaff ? 'حفظ التعديلات' : 'إضافة'}
                </button>
                <button type="button" onClick={() => setShowStaffForm(false)} className="px-5 py-2.5 bg-muted text-muted-foreground rounded-xl font-medium text-sm hover:text-foreground transition-all">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shift Form Modal */}
      {showShiftForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="font-bold text-foreground">إضافة وردية جديدة</h2>
              <button onClick={() => setShowShiftForm(false)} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleShiftSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">فرد الأمن *</label>
                <select
                  value={shiftForm.staffId}
                  onChange={(e) => setShiftForm((p) => ({ ...p, staffId: e.target.value }))}
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                >
                  <option value="">اختر فرد الأمن</option>
                  {staffList.filter((s) => s.isActive).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">نوع الوردية *</label>
                <div className="flex gap-2">
                  {SHIFTS.map((shift) => (
                    <button
                      key={shift}
                      type="button"
                      onClick={() => handleShiftTypeChange(shift)}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all ${shiftForm.shiftType === shift ? 'bg-accent text-accent-foreground border-accent' : 'bg-muted text-muted-foreground border-border hover:text-foreground'}`}
                    >
                      {shift}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">التاريخ *</label>
                <input
                  type="date"
                  value={shiftForm.shiftDate}
                  onChange={(e) => setShiftForm((p) => ({ ...p, shiftDate: e.target.value }))}
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">وقت البداية</label>
                  <input
                    type="time"
                    value={shiftForm.startTime}
                    onChange={(e) => setShiftForm((p) => ({ ...p, startTime: e.target.value }))}
                    className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">وقت النهاية</label>
                  <input
                    type="time"
                    value={shiftForm.endTime}
                    onChange={(e) => setShiftForm((p) => ({ ...p, endTime: e.target.value }))}
                    className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">البوابة</label>
                <select
                  value={shiftForm.gate}
                  onChange={(e) => setShiftForm((p) => ({ ...p, gate: e.target.value }))}
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {GATES.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">ملاحظات</label>
                <textarea
                  value={shiftForm.notes}
                  onChange={(e) => setShiftForm((p) => ({ ...p, notes: e.target.value }))}
                  rows={2}
                  className="w-full bg-input border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={shiftSubmitting}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-accent text-accent-foreground rounded-xl font-bold text-sm hover:opacity-90 transition-all disabled:opacity-50"
                >
                  {shiftSubmitting && <Loader2 size={14} className="animate-spin" />}
                  إضافة الوردية
                </button>
                <button type="button" onClick={() => setShowShiftForm(false)} className="px-5 py-2.5 bg-muted text-muted-foreground rounded-xl font-medium text-sm hover:text-foreground transition-all">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
